import { Formik } from 'formik'
import * as Yup from 'yup'
import AddCircleIcon from '@mui/icons-material/AddCircle'

import {
  Divider,
  Card,
  Grid,
  TextField,
  Container,
  CardContent,
  Tooltip,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  Alert,
  Button
} from '@mui/material'
import { ActionButton } from '../../../utils/ui_utils'

import { Close, Verified } from '@mui/icons-material'
import { useCheckCustomerExists } from '../../../modules/customer/customerHooks'
import { useEffect, useState } from 'react'

function CustomerDialog({
  contactPersonVisibility,
  visible,
  title,
  initialValues,
  okText,
  dialogMode,
  onSubmit,
  onCancel,
  contactPersonData,
  onContactPersonSearch,
  onAddContactPersonClick,
  isLedgerPresent,
  createLedgerClick,
  verifyMobile
}) {
  BigInt.prototype.toJSON = function () {
    return this.toString()
  }

  if (initialValues == undefined) initialValues = {}

  if (contactPersonData == undefined) contactPersonData = []

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required').min(2, 'Min length is 2 character'),
    mobile: Yup.string()
      .required('Required')
      .min(10, 'Min 10 digits')
      .max(10, 'Max 10 digits'),
    gstn: Yup.string()
      .min(15, 'Min 15 characters')
      .max(15, 'Max 15 characters'),
    email: Yup.string().email('Email is not valid')
    // contactperson: Yup.array().of(
    //   Yup.object().shape({
    //     _id: Yup.string(),
    //   })
    // ),
  })

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setStatus, setSubmitting }
  ) => {
    // Only submit if mobile is verified or unchanged in edit mode
    if (isMobileVerified || (okText === 'Update' && initialValues.mobile === values.mobile)) {
      onSubmit(values)
    }
  }

  // Initialize verification status as false for new entries, true for edit mode only if there's an existing mobile
  const [isMobileVerified, setIsMobileVerified] = useState(okText === 'Update' && initialValues?.mobile)
  const [mobileNoAlreadyExists, setMobileNoAlreadyExists] = useState(false)
  const { checkCustomerExistsData, checkCustomerExistsLoading } =
    useCheckCustomerExists()

  useEffect(() => {
    if (checkCustomerExistsData?.status === 200) {
      setMobileNoAlreadyExists(true)
      setIsMobileVerified(false)
    } else if (checkCustomerExistsData?.status === 404) {
      setMobileNoAlreadyExists(false)
      setIsMobileVerified(true)
    }
  }, [checkCustomerExistsData])

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={visible}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {title}
          <IconButton
            component="span"
            size="small"
            sx={{ ':hover': { backgroundColor: 'red', color: 'white' } }}
            onClick={() => {
              setIsMobileVerified(false)
              setMobileNoAlreadyExists(false)
              onCancel()
            }}
            variant="contained"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Container sx={{ marginBottom: 5 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              setFieldValue,
              values,
              status
            }) => (
              <form onSubmit={handleSubmit}>
                <Divider my={2} />
                <Card>
                  <CardContent>
                    <Grid container spacing={6} style={{ paddingTop: 10 }}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="name"
                          label="Name"
                          value={values.name}
                          error={Boolean(touched.name && errors.name)}
                          fullWidth
                          helperText={touched.name && errors.name}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="gstn"
                          label="GSTN"
                          value={values.gstn}
                          error={Boolean(touched.gstn && errors.gstn)}
                          fullWidth
                          helperText={touched.gstn && errors.gstn}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="email"
                          label="Email"
                          value={values.email}
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          helperText={touched.email && errors.email}
                          onBlur={handleBlur}
                          margin="dense"
                          type="email"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="mobile"
                          label="Mobile"
                          value={values.mobile}
                          error={Boolean(touched.mobile && errors.mobile)}
                          fullWidth
                          disabled={checkCustomerExistsLoading}
                          helperText={touched.mobile && errors.mobile}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={(e) => {
                            // Only allow digits in mobile field
                            const onlyDigits = e.target.value.replace(/[^0-9]/g, '');
                            
                            // If changing from a verified value, reset verification status
                            if (isMobileVerified && onlyDigits !== initialValues.mobile) {
                              setIsMobileVerified(false);
                            }
                            
                            // Update with digits only
                            e.target.value = onlyDigits;
                            handleChange(e);
                          }}
                          variant="outlined"
                          my={2}
                          inputProps={{ maxLength: 10 }}
                          InputProps={{
                            endAdornment:
                              // Show verified icon only if explicitly verified or unchanged in edit mode
                              (isMobileVerified && (okText !== 'Create' || values.mobile.length === 10)) ||
                              (okText === 'Update' &&
                                initialValues.mobile === values.mobile && initialValues.mobile) ? (
                                <Tooltip title="Verified number">
                                  <Verified color="primary" />
                                </Tooltip>
                              ) : (
                                <Button
                                  disabled={values.mobile.length < 10}
                                  size="small"
                                  onClick={() => verifyMobile(values.mobile)}
                                  color="primary"
                                  variant="outlined"
                                >
                                  Verify
                                </Button>
                              )
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      <Grid item md>
                        <TextField
                          size="small"
                          name="city"
                          label="City"
                          value={values.city}
                          error={Boolean(touched.city && errors.city)}
                          fullWidth
                          helperText={touched.city && errors.city}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md>
                        <TextField
                          size="small"
                          name="state"
                          label="State"
                          value={values.state}
                          error={Boolean(touched.state && errors.state)}
                          fullWidth
                          helperText={touched.state && errors.state}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                      <Grid item md>
                        <TextField
                          size="small"
                          name="country"
                          label="Country"
                          value={values.country}
                          error={Boolean(touched.country && errors.country)}
                          fullWidth
                          helperText={touched.country && errors.country}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md>
                        <TextField
                          size="small"
                          name="pincode"
                          label="Pincode"
                          value={values.pincode}
                          error={Boolean(touched.pincode && errors.pincode)}
                          fullWidth
                          helperText={touched.pincode && errors.pincode}
                          onBlur={handleBlur}
                          margin="dense"
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>
                    <Grid item md={12}>
                      <TextField
                        size="small"
                        name="address"
                        label="Address"
                        value={values.address}
                        error={Boolean(touched.address && errors.address)}
                        fullWidth
                        helperText={touched.address && errors.address}
                        onBlur={handleBlur}
                        margin="dense"
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                        multiline
                      />
                    </Grid>
                    {contactPersonVisibility && (
                      <Grid
                        container
                        spacing={6}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Grid item md={10}>
                          <Autocomplete
                            size="small"
                            multiple
                            id="contact_person_select"
                            name="contactperson"
                            options={contactPersonData}
                            getOptionLabel={(option) =>
                              `${option.first_name} (${option.mobile})`
                            }
                            isOptionEqualToValue={(option, value) =>
                              option._id === value._id
                            }
                            value={values.contactperson}
                            onChange={(e, value) => {
                              setFieldValue('contactperson', value)
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Contact Person"
                                placeholder="Search"
                                fullWidth
                                margin="dense"
                                //onChange={(e) => getContacts(e.target.value)}
                                onChange={(e) =>
                                  onContactPersonSearch(e.target.value)
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item md={1}>
                          <Tooltip title="Add Contact" placement="bottom">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={onAddContactPersonClick}
                            >
                              <AddCircleIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
                <Grid container justifyContent="flex-end">
                  {!isLedgerPresent && dialogMode == 'edit' && (
                    <Grid item pr={5}>
                      <ActionButton
                        color="success"
                        variant="outlined"
                        size="medium"
                        onClick={createLedgerClick}
                        mt={8}
                      >
                        Create Ledger
                      </ActionButton>
                    </Grid>
                  )}
                  {isMobileVerified ||
                  (okText === 'Update' &&
                    initialValues.mobile === values.mobile) ? (
                    <Grid item>
                      <ActionButton
                        variant="outlined"
                        size="medium"
                        type="submit"
                        mt={8}
                      >
                        {okText}
                      </ActionButton>
                    </Grid>
                  ) : (
                    <Grid item>
                      <Alert
                        severity={mobileNoAlreadyExists ? 'error' : 'info'}
                        sx={{ width: '100%' }}
                      >
                        {mobileNoAlreadyExists
                          ? 'This mobile number already exists'
                          : 'Verify Mobile Number to proceed'}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      </Dialog>
    </>
  )
}

export default CustomerDialog
